import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useTerms } from "../lib/BrandContext";

/**
 * First-run guided checklist shown at the top of the coach Home page.
 *
 * Steps are computed from `/api/coach/dashboard` totals — no extra API call:
 *   1. Create your first team          totals.teams > 0
 *   2. Add your first player           totals.players > 0
 *   3. Invite a parent                 totals.invites_created > 0
 *   4. Create your first fee           totals.charges_total > 0
 *
 * Auto-hides when all 4 are complete. Persistent-dismiss is intentionally
 * omitted per spec — "When onboarding is complete, automatically hide."
 */
export default function OnboardingChecklist({ totals }) {
  const { user } = useAuth();
  const { terms } = useTerms();
  const firstName = user?.name?.split(" ")[0] || terms.leader;

  const t = totals || {};
  const steps = [
    {
      id: "team",
      done: (t.teams || 0) > 0,
      label: `Create your first ${terms.team}`,
      to: "/coach/teams",
      cta: `Add ${terms.Team}`,
    },
    {
      id: "player",
      done: (t.players || 0) > 0,
      label: `Add your first ${terms.athlete}`,
      to: "/coach/teams",
      cta: `Add ${terms.Athlete}`,
    },
    {
      id: "invite",
      done: (t.invites_created || 0) > 0,
      label: "Invite a parent",
      to: "/coach/teams",
      cta: "Invite parent",
    },
    {
      id: "fee",
      done: (t.charges_total || 0) > 0,
      label: "Create your first fee",
      to: "/coach/charges/new",
      cta: "Add Fee",
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount >= steps.length) return null;

  // Next incomplete step drives the single primary CTA at the bottom.
  const next = steps.find((s) => !s.done);

  return (
    <section
      className="mt-5 rounded-2xl border border-brand-100 bg-brand-50/60 p-5 shadow-sm"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-ink-900">
            Welcome to Squad Fee, {firstName}!
          </h2>
          <p className="mt-1 text-sm text-ink-600">
            Let&apos;s get your {terms.team} ready — {doneCount} of {steps.length} done.
          </p>
        </div>
        <span
          className="shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-brand shadow-sm"
          data-testid="onboarding-progress"
        >
          {doneCount}/{steps.length}
        </span>
      </div>

      <ol className="mt-4 space-y-2" data-testid="onboarding-steps">
        {steps.map((s) => (
          <li
            key={s.id}
            className="flex items-center gap-3 text-sm"
            data-testid={`onboarding-step-${s.id}`}
            data-done={s.done ? "true" : "false"}
          >
            <span
              className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${
                s.done
                  ? "border-emerald-600 bg-emerald-600 text-white"
                  : "border-ink-300 bg-white"
              }`}
              aria-hidden="true"
            >
              {s.done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
            </span>
            <span
              className={`min-w-0 flex-1 font-medium ${
                s.done ? "text-ink-500 line-through" : "text-ink-900"
              }`}
            >
              {s.label}
            </span>
          </li>
        ))}
      </ol>

      {next ? (
        <div className="mt-4">
          <Link to={next.to} data-testid="onboarding-next-cta">
            <button
              type="button"
              className="inline-flex h-10 items-center rounded-lg bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-hover"
            >
              {next.cta}
            </button>
          </Link>
        </div>
      ) : null}
    </section>
  );
}
